import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import API from '../../api';

const CONSENTS = [
  {
    key: 'consent_data_processing',
    labelKey: 'kyc.privacy.consent_1',
    required: true,
  },
  {
    key: 'consent_terms_conditions',
    labelKey: 'kyc.privacy.consent_2',
    required: true,
  },
  {
    key: 'consent_marketing',
    labelKey: 'kyc.privacy.consent_3',
    required: false,
  },
  {
    key: 'consent_profiling',
    labelKey: 'kyc.privacy.consent_4',
    required: false,
  },
];

const PrivacySettings = ({ userId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!userId) return;
    API.get(`/users/${userId}/privacy`)
      .then(res => {
        setData(res.data);
        setForm({
          consent_data_processing: !!res.data.consent_data_processing,
          consent_terms_conditions: !!res.data.consent_terms_conditions,
          consent_marketing: !!res.data.consent_marketing,
          consent_profiling: !!res.data.consent_profiling,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSave = async () => {
    if (!form.consent_data_processing || !form.consent_terms_conditions) {
      setError(t('kyc.privacy.error_required'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        consent_data_processing: true,
        consent_terms_conditions: true,
      };
      await API.put(`/users/${userId}/privacy`, payload);
      setData(prev => ({ ...prev, ...form, consents_accepted_at: new Date().toISOString().slice(0, 19).replace('T', ' ') }));
      setEditing(false);
      setSuccess(t('settings.privacy_save_success'));
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError(t('settings.privacy_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (data) {
      setForm({
        consent_data_processing: !!data.consent_data_processing,
        consent_terms_conditions: !!data.consent_terms_conditions,
        consent_marketing: !!data.consent_marketing,
        consent_profiling: !!data.consent_profiling,
      });
    }
    setEditing(false);
    setError('');
  };

  if (loading) return null;

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundCard, borderRadius: UI.borderRadius.md, padding: UI.spacing.xl, marginBottom: UI.spacing.xl, boxShadow: UI.shadow.card }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.lg, paddingBottom: UI.spacing.sm, borderBottom: '1px solid #333' }}>
        <h3 style={{ color: SEMANTIC.ctaPrimary, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, margin: 0 }}>
          {t('settings.section_privacy')}
        </h3>
        {!editing && (
          <button
            onClick={() => { setEditing(true); setError(''); setSuccess(''); }}
            style={{ background: 'transparent', border: `1px solid ${SEMANTIC.ctaPrimary}`, color: SEMANTIC.ctaPrimary, borderRadius: UI.borderRadius.sm, padding: '6px 14px', fontSize: UI.fontSize.sm, cursor: 'pointer' }}
          >
            <i className="bi bi-pencil" style={{ marginRight: '6px' }} />
            {t('settings.edit_btn')}
          </button>
        )}
      </div>

      {success && (
        <div style={{ backgroundColor: `${SEMANTIC.ctaPrimary}18`, border: `1px solid ${SEMANTIC.ctaPrimary}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.ctaPrimary, textAlign: 'center' }}>
          {success}
        </div>
      )}

      {/* READ-ONLY */}
      {!editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: UI.spacing.md }}>
          {data?.consents_accepted_at && (
            <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
              {t('settings.privacy_updated_on', { date: data.consents_accepted_at })}
            </p>
          )}
          {CONSENTS.map(c => {
            const active = data ? !!data[c.key] : false;
            return (
              <div key={c.key} style={{ display: 'flex', alignItems: 'flex-start', gap: UI.spacing.md, padding: UI.spacing.md, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: UI.borderRadius.sm }}>
                <i
                  className={`bi ${active ? 'bi-check-circle-fill' : 'bi-x-circle'}`}
                  style={{ fontSize: '20px', color: active ? SEMANTIC.ctaPrimary : SEMANTIC.textDisabled, flexShrink: 0, marginTop: '1px' }}
                />
                <div>
                  <div>
                    <span style={{ color: active ? SEMANTIC.textPrimary : SEMANTIC.textDisabled, fontSize: UI.fontSize.sm, lineHeight: '1.5' }}>
                      {t(c.labelKey)}
                    </span>
                    {c.required && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                        <i className="bi bi-lock-fill" style={{ fontSize: '10px', color: SEMANTIC.textDisabled }} />
                        <span style={{ color: SEMANTIC.textDisabled, fontSize: UI.fontSize.xs }}>{t('settings.privacy_required_lock')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.xs, margin: 0 }}>
            <strong style={{ color: SEMANTIC.ctaPrimary }}>*</strong> {t('kyc.privacy.required_note')}
          </p>
        </div>
      )}

      {/* EDIT FORM */}
      {editing && (
        <div>
          {error && (
            <div style={{ backgroundColor: 'rgba(255,77,77,0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: UI.spacing.md, marginBottom: UI.spacing.xl }}>
            {CONSENTS.map(c => {
              if (c.required) {
                // Obbligatori: sempre spuntati, non modificabili
                return (
                  <div
                    key={c.key}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: UI.spacing.md,
                      padding: UI.spacing.lg,
                      backgroundColor: `${SEMANTIC.ctaPrimary}10`,
                      border: `2px solid ${SEMANTIC.ctaPrimary}55`,
                      borderRadius: UI.borderRadius.sm,
                      opacity: 0.75,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked
                      disabled
                      style={{ width: '20px', height: '20px', accentColor: SEMANTIC.ctaPrimary, flexShrink: 0, marginTop: '2px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.sm, lineHeight: '1.6' }}>
                        {t(c.labelKey)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <i className="bi bi-lock-fill" style={{ fontSize: '11px', color: SEMANTIC.textDisabled }} />
                        <span style={{ color: SEMANTIC.textDisabled, fontSize: UI.fontSize.xs }}>
                          {t('settings.privacy_required_lock')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }

              // Facoltativi: modificabili
              const checked = !!form[c.key];
              return (
                <label
                  key={c.key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: UI.spacing.md,
                    padding: UI.spacing.lg,
                    backgroundColor: checked ? `${SEMANTIC.ctaPrimary}12` : 'rgba(255,255,255,0.03)',
                    border: `2px solid ${checked ? SEMANTIC.ctaPrimary : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: UI.borderRadius.sm,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onClick={() => setForm(prev => ({ ...prev, [c.key]: !prev[c.key] }))}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    style={{ width: '20px', height: '20px', accentColor: SEMANTIC.ctaPrimary, flexShrink: 0, marginTop: '2px', cursor: 'pointer' }}
                  />
                  <span style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.sm, lineHeight: '1.6' }}>
                    {t(c.labelKey)}
                  </span>
                </label>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: UI.spacing.md }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 1, padding: UI.spacing.md, backgroundColor: saving ? SEMANTIC.backgroundDisabled : SEMANTIC.ctaPrimary, color: saving ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimaryText, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              style={{ padding: `${UI.spacing.md} ${UI.spacing.xl}`, backgroundColor: 'transparent', color: SEMANTIC.textSecondary, border: '2px solid #555', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, cursor: 'pointer' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySettings;
